import { useState } from 'react';
import { Empty, Table, Tag, Tooltip, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { useDeviceAudit } from '@/hooks/useDevices';
import type { DeviceAuditEntryDto } from '@/types/device';

const { Text } = Typography;

interface Props {
  deviceId: number;
}

/**
 * Mapeo de eventTypes conocidos a colores. Cuando aparece uno nuevo no rompe (default gris).
 */
const EVENT_COLORS: Record<string, string> = {
  'Device.Created': 'success',
  'Device.Updated': 'processing',
  'Device.Deactivated': 'warning',
  'Device.Deleted': 'error',
  'EntityCreated': 'success',
  'EntityUpdated': 'processing',
  'EntityDeleted': 'error',
};

export function DeviceAuditTab({ deviceId }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { data, isLoading } = useDeviceAudit(deviceId, page, pageSize);

  const columns: TableProps<DeviceAuditEntryDto>['columns'] = [
    {
      title: 'Cuando',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v) => (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm:ss')}>
          {dayjs(v).fromNow()}
        </Tooltip>
      ),
    },
    {
      title: 'Evento',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 200,
      render: (v: string) => <Tag color={EVENT_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Usuario',
      dataIndex: 'userName',
      key: 'userName',
      width: 200,
      render: (v) => v ?? <Text type="secondary" italic>Sistema</Text>,
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      key: 'ip',
      width: 130,
      render: (v) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Detalles',
      key: 'details',
      render: (_, a) => {
        if (a.additionalInfo) {
          return <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: a.additionalInfo }}>{a.additionalInfo}</Text>;
        }
        if (a.newValues || a.oldValues) {
          return <Text type="secondary" style={{ fontSize: 12 }}>Cambios registrados (ver expand)</Text>;
        }
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  if (!isLoading && (data?.totalCount ?? 0) === 0) {
    return <Empty description="No hay eventos de audit para este dispositivo." />;
  }

  return (
    <Table<DeviceAuditEntryDto>
      rowKey="id"
      columns={columns}
      dataSource={data?.items ?? []}
      loading={isLoading}
      expandable={{
        rowExpandable: (a) => !!(a.oldValues || a.newValues),
        expandedRowRender: (a) => (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {a.oldValues && (
              <div>
                <Text strong>Antes:</Text>
                <pre style={{
                  background: 'var(--color-bg-elevated, #fafafa)',
                  padding: 8, borderRadius: 4, fontSize: 11, marginTop: 4,
                  maxHeight: 240, overflow: 'auto',
                }}>
                  {tryFormatJson(a.oldValues)}
                </pre>
              </div>
            )}
            {a.newValues && (
              <div>
                <Text strong>Despues:</Text>
                <pre style={{
                  background: 'var(--color-bg-elevated, #fafafa)',
                  padding: 8, borderRadius: 4, fontSize: 11, marginTop: 4,
                  maxHeight: 240, overflow: 'auto',
                }}>
                  {tryFormatJson(a.newValues)}
                </pre>
              </div>
            )}
          </div>
        ),
      }}
      pagination={{
        current: page,
        pageSize,
        total: data?.totalCount ?? 0,
        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        showSizeChanger: true,
        pageSizeOptions: [25, 50, 100, 200],
        showTotal: (total) => `${total} eventos`,
      }}
      size="small"
    />
  );
}

function tryFormatJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
